import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import {
  ScanLine, Plus, Trash2, PackageCheck, AlertTriangle, PackageX, Clock,
  CheckCircle2, FileSpreadsheet, ScrollText, Scale, ChevronsUpDown, Check,
  GitCompare, MapPin, Users, X, StickyNote, RotateCcw, RefreshCw,
} from "lucide-react";
import SafetyShell from "../safety/SafetyShell";
import { fmtDateTime, toISTInputValue, istInputToUTC } from "../lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

function ProductCombobox({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState([]);
  useEffect(() => {
    axios.get(`${API}/stock-count/product-codes`, auth()).then((r) => setCodes(r.data?.codes || [])).catch(() => {});
  }, []);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="flex-1 justify-between font-normal" data-testid={testId}>
          <span className={`truncate ${value ? "font-mono text-xs text-zinc-900" : "text-zinc-400"}`}>{value || "Search product code…"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search code…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No matching product code.</CommandEmpty>
            <CommandGroup>
              {codes.map((c) => (
                <CommandItem key={c} value={c} onSelect={() => { onChange(c); setOpen(false); }} data-testid={`${testId}-opt-${c}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === c ? "opacity-100" : "opacity-0"}`} />
                  <span className="font-mono text-xs">{c}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function StockCount({ user, onLogout }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [liveVar, setLiveVar] = useState(null); // { [session_id]: {net_variance, variances} }
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // session detail object
  const [newName, setNewName] = useState("");
  const [scanInput, setScanInput] = useState("");
  const [zone, setZone] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [manualQty, setManualQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [tab, setTab] = useState("counted");
  const [scanFilter, setScanFilter] = useState({ pallet: "", product: "", zone: "", method: "" });
  const [countedFilter, setCountedFilter] = useState("");
  const [missingFilter, setMissingFilter] = useState({ pallet: "", product: "" });
  const [perms, setPerms] = useState([]);
  const canManage = perms.includes("inventory.manage") || perms.includes("stock.count");
  const canCutover = perms.includes("inventory.manage");
  const [bulkSel, setBulkSel] = useState(new Set());
  const [bulkCode, setBulkCode] = useState("");
  const [bulkQty, setBulkQty] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);

  const undoAutoRetire = async () => {
    if (!window.confirm("Undo auto-dispatch retirement?\n\nThis restores every pallet the (now-disabled) auto-retire flipped to DISPATCHED back to its prior state (PRODUCED/PACKED), so they reappear in Live Production. Safe & reversible — it only affects pallets that feature flagged.")) return;
    setUndoBusy(true);
    try {
      const r = await axios.post(`${API}/stock-count/auto-retire/rollback`, {}, auth());
      const d = r.data || {};
      if (d.reverted === 0) {
        toast.success("Nothing to undo — no auto-retired pallets found.");
      } else {
        toast.success(`Restored ${d.reverted} pallet(s): ${d.restored_to_produced} → Produced, ${d.restored_to_packed} → Packed, ${d.partial_cleared} partial cleared.`);
      }
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    } finally {
      setUndoBusy(false);
    }
  };

  const loadSessions = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock-count/sessions`, auth());
      setSessions(r.data.items || []);
    } catch { toast.error("Failed to load sessions"); }
    finally { setLoading(false); }
    // Recompute current (live) variance against today's ERP data in the background.
    setLiveVar(null);
    axios.get(`${API}/stock-count/live-variance`, auth())
      .then((r) => setLiveVar(r.data?.items || {}))
      .catch(() => setLiveVar({}));
  };

  const openSession = async (id) => {
    try {
      const r = await axios.get(`${API}/stock-count/sessions/${id}`, auth());
      setActive(r.data);
      setTab("counted");
    } catch { toast.error("Failed to open session"); }
  };

  const refreshActive = async () => {
    if (!active) return;
    try { const r = await axios.get(`${API}/stock-count/sessions/${active.session.id}`, auth()); setActive(r.data); } catch { /* ignore */ }
  };

  const photoUrl = (fid) => `${API}/files/download/${fid}?token=${localStorage.getItem("token")}&inline=true`;
  const [resolveInputs, setResolveInputs] = useState({}); // uid -> { code, qty }
  const setRi = (uid, patch) => setResolveInputs((s) => ({ ...s, [uid]: { ...(s[uid] || {}), ...patch } }));

  const resolveUnident = async (uid) => {
    const ri = resolveInputs[uid] || {};
    if (!ri.code) { toast.error("Pick a product code"); return; }
    if (!Number(ri.qty)) { toast.error("Enter a quantity"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/stock-count/sessions/${active.session.id}/unidentified/${uid}/resolve`,
        { product_code: ri.code, qty: Number(ri.qty) }, auth());
      toast.success("Identified & rolled into the count");
      setResolveInputs((s) => { const n = { ...s }; delete n[uid]; return n; });
      await refreshActive();
    } catch (e) { toast.error(e.response?.data?.detail || "Resolve failed"); }
    finally { setBusy(false); }
  };

  const resolveNote = async (uid) => {
    const ri = resolveInputs[uid] || {};
    const note = (ri.noteText || "").trim();
    if (!note) { toast.error("Enter a note"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/stock-count/sessions/${active.session.id}/unidentified/${uid}/resolve-note`,
        { note }, auth());
      toast.success("Closed with a note (not counted)");
      setResolveInputs((s) => { const n = { ...s }; delete n[uid]; return n; });
      await refreshActive();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save note"); }
    finally { setBusy(false); }
  };

  const resolvePallet = async (uid, palletId = null) => {
    const ri = resolveInputs[uid] || {};
    const short = (ri.shortId || "").trim();
    if (!palletId && !short) { toast.error("Enter the pallet's short number"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/stock-count/sessions/${active.session.id}/unidentified/${uid}/resolve-pallet`,
        palletId ? { pallet_id: palletId } : { short }, auth()).then((r) => r.data);
      if (res.needs_disambiguation) {
        setRi(uid, { palletCandidates: res.candidates });
        toast("Multiple pallets match — pick one");
        return;
      }
      toast.success(`Linked to ${res.pallet_id} · ${res.product_code || "(no product)"} (${res.qty || 0} pcs)${res.already_scanned ? " — already counted" : ""}`);
      setResolveInputs((s) => { const n = { ...s }; delete n[uid]; return n; });
      await refreshActive();
    } catch (e) { toast.error(e.response?.data?.detail || "Resolve failed"); }
    finally { setBusy(false); }
  };
  const deleteUnident = async (uid) => {
    if (!window.confirm("Remove this unidentified item?")) return;
    try { await axios.delete(`${API}/stock-count/sessions/${active.session.id}/unidentified/${uid}`, auth()); await refreshActive(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
  };

  const toggleBulk = (uid) => setBulkSel((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const bulkResolve = async () => {
    if (!bulkCode) { toast.error("Pick a product code"); return; }
    if (!Number(bulkQty)) { toast.error("Enter a quantity"); return; }
    if (bulkSel.size === 0) { toast.error("Select at least one item"); return; }
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/stock-count/sessions/${active.session.id}/unidentified/bulk-resolve`,
        { ids: [...bulkSel], product_code: bulkCode, qty: Number(bulkQty) }, auth());
      toast.success(`Resolved ${r.data.resolved} item(s) → ${bulkCode}`);
      setBulkSel(new Set()); setBulkCode(""); setBulkQty("");
      await refreshActive();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk resolve failed"); }
    finally { setBulkBusy(false); }
  };

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => {
    axios.get(`${API}/auth/me/permissions`, auth())
      .then((r) => setPerms(r.data?.effective_permissions || []))
      .catch(() => {});
  }, []);

  const createSession = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/stock-count/sessions`, { name: newName }, auth());
      setNewName("");
      await loadSessions();
      await openSession(r.data.id);
      toast.success("Count session started — as-of frozen at now");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create"); }
    finally { setBusy(false); }
  };

  const doScan = async () => {
    const pid = scanInput.trim();
    if (!pid || !active) return;
    if (!zone.trim()) { toast.error("Enter a zone / aisle before counting"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/stock-count/sessions/${active.session.id}/scan`, { pallet_id: pid, zone: zone.trim(), capture_method: "typed_id" }, auth());
      if (r.data.duplicate) toast.warning(`Already scanned: ${pid}`);
      else {
        const s = r.data.scan;
        if (s.included) toast.success(`+${s.qty} ${s.product_code} (${s.pallet_id})`);
        else toast.warning(`${s.pallet_id} excluded — ${s.reason}`);
      }
      setScanInput("");
      await refreshActive();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setBusy(false); }
  };

  const addManual = async () => {
    if (!manualCode.trim() || !manualQty || !active) return;
    if (!zone.trim()) { toast.error("Enter a zone / aisle before counting"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/stock-count/sessions/${active.session.id}/manual`,
        { product_code: manualCode.trim(), qty: Number(manualQty), zone: zone.trim() }, auth());
      setManualCode(""); setManualQty("");
      await refreshActive();
      toast.success("Loose entry added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const removeScan = async (scanId) => {
    if (!isOpen && !window.confirm("Delete this entry from a FINALIZED count? The reconciliation will be recalculated and the change logged.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/stock-count/sessions/${active.session.id}/scans/${scanId}`, auth());
      await refreshActive();
      if (!isOpen) toast.success("Entry removed — reconciliation refreshed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // Correct an existing entry (product code / qty) — works on open or finalized counts
  const [editRow, setEditRow] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [editQty, setEditQty] = useState("");
  const openEdit = (sc) => { setEditRow(sc); setEditCode(sc.product_code || ""); setEditQty(String(sc.qty ?? "")); };
  const [corrLogOpen, setCorrLogOpen] = useState(false);
  const [corrLog, setCorrLog] = useState([]);
  const [corrBusy, setCorrBusy] = useState(false);
  const openCorrLog = async () => {
    setCorrLogOpen(true);
    setCorrBusy(true);
    try {
      const r = await axios.get(`${API}/stock-count/sessions/${active.session.id}/corrections-log`, auth());
      setCorrLog(r.data.entries || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load log"); }
    finally { setCorrBusy(false); }
  };
  const saveEdit = async () => {
    if (!editRow) return;
    const body = {};
    if (editCode.trim() && editCode.trim() !== editRow.product_code) body.product_code = editCode.trim();
    if (editQty !== "" && Number(editQty) !== editRow.qty) body.qty = Number(editQty);
    if (Object.keys(body).length === 0) { setEditRow(null); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/stock-count/sessions/${active.session.id}/scans/${editRow.id}`, body, auth());
      setEditRow(null);
      await refreshActive();
      toast.success(isOpen ? "Entry updated" : "Entry corrected — reconciliation refreshed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const finalize = async () => {
    const pendU = sm.total_unidentified || 0;
    const pendS = sm.total_skips || 0;
    if (pendU > 0 || pendS > 0) {
      const parts = [];
      if (pendU > 0) parts.push(`${pendU} unidentified item(s) still pending — excluded from totals`);
      if (pendS > 0) parts.push(`${pendS} skipped pallet(s) not yet counted`);
      if (!window.confirm(`Before finalizing:\n\n• ${parts.join("\n• ")}\n\nThese won't be counted. Finalize anyway?`)) return;
    }
    setFinalizing(true);
    try {
      const r = await axios.post(`${API}/stock-count/sessions/${active.session.id}/finalize`, {}, auth());
      const de = r.data?.dispatched_explained || 0;
      toast.success(de > 0
        ? `Reconciliation generated · ${de} pallet(s) auto-marked dispatched (${r.data.dispatched_explained_pieces || 0} pcs)`
        : "Reconciliation generated from scan count");
      navigate(`/safety/inventory/reconcile?recon=${r.data.reconciliation_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Finalize failed"); }
    finally { setFinalizing(false); }
  };

  const [cutoverBusy, setCutoverBusy] = useState(false);
  const [cutoverDialog, setCutoverDialog] = useState(false);
  const [cutoverEff, setCutoverEff] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [reconNotes, setReconNotes] = useState({});          // {product_code: note}
  const [reconNoteDraft, setReconNoteDraft] = useState({});  // local editing
  const [savingNote, setSavingNote] = useState(null);
  const [resolHistory, setResolHistory] = useState({});      // {product_code: [entries]}
  const [resolveDlg, setResolveDlg] = useState({ open: false, item: null, mode: null });
  const [resolveNoteText, setResolveNoteText] = useState("");
  const [reasonPresets, setReasonPresets] = useState([]);
  const [newPresetLabel, setNewPresetLabel] = useState("");
  const [addingPreset, setAddingPreset] = useState(false);
  const [compareBusy, setCompareBusy] = useState(false);
  const [compareSnapshot, setCompareSnapshot] = useState(null); // frozen finalize snapshot (reference only)
  const [resolvingVar, setResolvingVar] = useState(null); // product_code currently being resolved

  // Compare 2+ sessions (independent counts of the same stock)
  const [cmpSel, setCmpSel] = useState(new Set());
  const [cmpData, setCmpData] = useState(null);
  const [cmpBusy, setCmpBusy] = useState(false);
  const [cmpOpen, setCmpOpen] = useState(false);
  const toggleCmp = (id) => setCmpSel((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const runCompare = async () => {
    const ids = [...cmpSel];
    if (ids.length < 2) { toast.error("Tick at least 2 counts to compare"); return; }
    setCmpBusy(true);
    setCmpOpen(true);
    try {
      const r = await axios.post(`${API}/stock-count/compare-sessions`, { session_ids: ids }, auth());
      setCmpData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compare failed");
      setCmpOpen(false);
    } finally {
      setCmpBusy(false);
    }
  };
  const mergeCompared = async () => {
    const ids = [...cmpSel];
    if (ids.length < 2) return;
    if (!window.confirm(`Create a new master count from ${ids.length} counts? It merges the UNION of all pallets (deduped — no double counting) into one open session you can review and finalize.`)) return;
    try {
      const r = await axios.post(`${API}/stock-count/merge-sessions`, { session_ids: ids }, auth());
      toast.success(`Master count created — ${r.data.pallets_merged} pallets merged from ${r.data.sessions_merged} counts`);
      setCmpOpen(false);
      setCmpSel(new Set());
      await loadSessions();
      openSession(r.data.session_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    }
  };
  const openCompare = async () => {
    setCompareOpen(true); setCompareBusy(true); setCompareData(null); setCompareSnapshot(null);
    const closed = active?.session?.status === "closed" && active?.session?.reconciliation_id;
    try {
      // Primary view = CURRENT position: recompute against today's ERP data (parsed
      // packing lists, dispatches, adjustments) instead of the frozen finalize snapshot.
      const r = await axios.get(`${API}/stock-count/sessions/${active.session.id}/compare?live=true`, auth());
      setCompareData(r.data);
      try {
        const rn = await axios.get(`${API}/stock-count/sessions/${active.session.id}/recon-notes`, auth());
        setReconNotes(rn.data.notes || {}); setReconNoteDraft({});
      } catch (_) { /* notes optional */ }
      try {
        const rh = await axios.get(`${API}/stock-count/resolution-history`, auth());
        setResolHistory(rh.data.history || {});
      } catch (_) { /* history optional */ }
      // For a finalized count, also pull the frozen finalize snapshot for reference.
      if (closed) {
        try {
          const s = await axios.get(`${API}/stock-count/sessions/${active.session.id}/compare`, auth());
          setCompareSnapshot(s.data);
        } catch (_) { /* snapshot is optional */ }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Compare failed"); }
    finally { setCompareBusy(false); }
  };
  const fmtIST = (ts) => {
    try {
      return new Date(ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ts; }
  };
  const openResolve = (item, mode) => { setResolveDlg({ open: true, item, mode }); setResolveNoteText(""); loadPresets(); };
  const loadPresets = async () => {
    try {
      const r = await axios.get(`${API}/stock-count/reason-presets`, auth());
      setReasonPresets(r.data.presets || []);
    } catch (_) { /* presets optional */ }
  };
  const applyPreset = (label) => {
    setResolveNoteText((prev) => {
      const t = prev.trim();
      return t ? `${t} — ${label}` : label;
    });
  };
  const addPreset = async () => {
    const label = newPresetLabel.trim();
    if (!label) return;
    setAddingPreset(true);
    try {
      const r = await axios.post(`${API}/stock-count/reason-presets`, { label }, auth());
      setNewPresetLabel("");
      await loadPresets();
      if (r.data?.preset?.label) applyPreset(r.data.preset.label);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add reason"); }
    finally { setAddingPreset(false); }
  };
  const deletePreset = async (id) => {
    try {
      await axios.delete(`${API}/stock-count/reason-presets/${id}`, auth());
      setReasonPresets((prev) => prev.filter((p) => p.id !== id));
    } catch { toast.error("Could not remove reason"); }
  };
  const confirmResolve = async () => {
    const { item, mode } = resolveDlg;
    if (!item) return;
    const note = resolveNoteText.trim();
    if (!note) { toast.error("Please write a note explaining this change"); return; }
    const qty = item.variance; // physical − erp
    setResolvingVar(item.product_code);
    try {
      await axios.post(`${API}/stock-count/resolve-variance`,
        { product_code: item.product_code, mode, qty, session_id: active?.session?.id, reason: note }, auth());
      toast.success(`${item.product_code} resolved via ${mode === "opening" ? "opening balance" : "adjustment"}`);
      setResolveDlg({ open: false, item: null, mode: null });
      await openCompare();     // re-fetch live so the row updates
      await refreshActive();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
    finally { setResolvingVar(null); }
  };
  const saveReconNote = async (code) => {
    if (!active?.session?.id) return;
    const note = ((reconNoteDraft[code] ?? reconNotes[code]) || "").trim();
    setSavingNote(code);
    try {
      await axios.post(`${API}/stock-count/sessions/${active.session.id}/recon-notes`,
        { product_code: code, note }, auth());
      setReconNotes((prev) => ({ ...prev, [code]: note }));
      setReconNoteDraft((prev) => { const n = { ...prev }; delete n[code]; return n; });
      toast.success(note ? "Note saved" : "Note cleared");
    } catch { toast.error("Could not save note"); }
    finally { setSavingNote(null); }
  };

  const useAsCutover = () => {
    setCutoverEff(toISTInputValue(active?.session?.as_of) || "");
    setCutoverDialog(true);
  };
  const confirmUseAsCutover = async () => {
    if (!cutoverEff) { toast.error("Pick the cutover date & time"); return; }
    setCutoverBusy(true);
    try {
      const r = await axios.post(`${API}/stock-count/sessions/${active.session.id}/use-as-cutover`,
        { effective_date: istInputToUTC(cutoverEff) }, auth());
      toast.success(`Draft CUTOVER v${r.data.revision_no} created (${r.data.line_count} products) — review & activate`);
      setCutoverDialog(false);
      navigate("/safety/inventory/setup");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create cutover baseline"); }
    finally { setCutoverBusy(false); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this count session and all its scans?")) return;
    try {
      await axios.delete(`${API}/stock-count/sessions/${id}`, auth());
      if (active?.session?.id === id) setActive(null);
      await loadSessions();
      toast.success("Session deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const sm = active?.summary || {};
  const isOpen = active?.session?.status === "open";
  const scanZones = [...new Set((active?.scans || []).map((s) => s.zone).filter(Boolean))].sort();
  const filteredScans = (active?.scans || []).filter((s) => {
    const f = scanFilter;
    if (f.pallet && !String(s.pallet_id || "").toLowerCase().includes(f.pallet.toLowerCase())) return false;
    if (f.product && !String(s.product_code || "").toLowerCase().includes(f.product.toLowerCase())) return false;
    if (f.zone && (s.zone || "") !== f.zone) return false;
    if (f.method) {
      if (f.method === "manual") { if (s.source === "manual") return true; return false; }
      if (s.source === "manual") return false;
      if ((s.capture_method || "unknown") !== f.method) return false;
    }
    return true;
  });
  const filteredCounted = (active?.per_product || []).filter((r) =>
    !countedFilter || String(r.product_code || "").toLowerCase().includes(countedFilter.toLowerCase()));
  const filteredMissing = (active?.missing || []).filter((m) => {
    if (missingFilter.pallet && !String(m.pallet_id || "").toLowerCase().includes(missingFilter.pallet.toLowerCase())) return false;
    if (missingFilter.product && !String(m.product_code || "").toLowerCase().includes(missingFilter.product.toLowerCase())) return false;
    return true;
  });

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Inventory", to: "/safety/inventory" }, { label: "Stock Count" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Inventory", to: "/safety/inventory", depth: 1 },
        { label: "Reconcile", to: "/safety/inventory/reconcile", depth: 2 },
        { label: "Stock Count (scan)", active: true, depth: 2 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="stock-count-page">
        <div className="sf-surface p-4 mb-4 flex items-center gap-3">
          <ScanLine className="w-5 h-5 text-orange-600" />
          <div>
            <h1 className="text-xl font-bold tracking-tight">Scan-based Stock Count</h1>
            <p className="text-xs text-zinc-500">Count physical stock by scanning pallet QR cards — the authoritative source for reconciliation.</p>
          </div>
          {canCutover && (
            <Button variant="outline" size="sm" className="ml-auto border-amber-300 text-amber-800 hover:bg-amber-50" onClick={undoAutoRetire} disabled={undoBusy} data-testid="sc-undo-autoretire-btn" title="Restore pallets that the disabled auto-dispatch feature flipped to DISPATCHED">
              <RotateCcw className={`w-4 h-4 mr-1.5 ${undoBusy ? "animate-spin" : ""}`} /> {undoBusy ? "Restoring…" : "Undo auto-dispatch"}
            </Button>
          )}
        </div>

        {!active ? (
          <div className="max-w-4xl mx-auto space-y-4">
            <div className="sf-surface p-4 flex items-end gap-2">
              <div className="flex-1">
                <label className="text-xs font-medium text-zinc-500 block mb-1">New count session</label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Warehouse A — Jun 30 shift-end" data-testid="sc-new-name" />
              </div>
              <Button onClick={createSession} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="sc-create-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Start count
              </Button>
            </div>

            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-4 py-2.5 border-b flex items-center gap-2">
                <ScrollText className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-semibold">Count history</span>
                <span className="text-xs text-zinc-400">— tick 2+ counts of the same stock to compare variance</span>
                <Button
                  size="sm"
                  variant={cmpSel.size >= 2 ? "default" : "outline"}
                  className={`ml-auto ${cmpSel.size >= 2 ? "bg-orange-600 hover:bg-orange-700" : ""}`}
                  disabled={cmpSel.size < 2 || cmpBusy}
                  onClick={runCompare}
                  data-testid="sc-compare-sessions-btn"
                >
                  <GitCompare className="w-4 h-4 mr-1.5" />
                  Compare{cmpSel.size ? ` (${cmpSel.size})` : ""}
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 w-8"></th>
                    <th className="p-3 text-left">Session</th>
                    <th className="p-3 text-left">As of</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-right">Coverage</th>
                    <th className="p-3 text-right">Net variance</th>
                    <th className="p-3 text-right" title="Recomputed against current ERP data — drifts from the frozen net variance if ERP data changed since finalize">Current variance</th>
                    <th className="p-3 text-left">Counters</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan="9" className="p-8 text-center text-gray-400">Loading…</td></tr>}
                  {!loading && sessions.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-gray-500" data-testid="sc-no-sessions">No count sessions yet.</td></tr>}
                  {sessions.map((s) => {
                    const m = s.metrics || {};
                    const cov = m.coverage_pct;
                    const nv = m.net_variance;
                    const lv = liveVar ? liveVar[s.id] : undefined;
                    const cv = lv ? lv.net_variance : null;
                    return (
                    <tr key={s.id} className={`border-b hover:bg-orange-50/40 ${cmpSel.has(s.id) ? "bg-orange-50/60" : ""}`} data-testid={`sc-session-${s.id}`}>
                      <td className="p-3 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-orange-600 cursor-pointer"
                          checked={cmpSel.has(s.id)}
                          onChange={() => toggleCmp(s.id)}
                          data-testid={`sc-cmp-check-${s.id}`}
                          title="Select for comparison"
                        />
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{s.name}</div>
                        {s.closed_by && <div className="text-[11px] text-zinc-400">finalized by {s.closed_by} · {fmtDateTime(s.closed_at)}</div>}
                        {s.status === "open" && (s.unidentified_pending > 0 || s.skips_pending > 0) && (
                          <div className="flex gap-1.5 mt-1" data-testid={`sc-session-pending-${s.id}`}>
                            {s.unidentified_pending > 0 && (
                              <Badge className="bg-amber-100 text-amber-700 text-[11px]" data-testid={`sc-session-unident-badge-${s.id}`}>
                                {s.unidentified_pending} unidentified
                              </Badge>
                            )}
                            {s.skips_pending > 0 && (
                              <Badge className="bg-sky-100 text-sky-700 text-[11px]" data-testid={`sc-session-skip-badge-${s.id}`}>
                                {s.skips_pending} skipped
                              </Badge>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-xs text-gray-500">{fmtDateTime(s.as_of)}</td>
                      <td className="p-3">
                        <Badge className={s.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}>{s.status}</Badge>
                      </td>
                      <td className="p-3 text-right">
                        {s.status === "closed" && cov != null
                          ? <span className={cov >= 95 ? "text-emerald-600" : cov >= 70 ? "text-amber-600" : "text-red-600"}>{cov}%</span>
                          : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="p-3 text-right">
                        {s.status === "closed" && nv != null
                          ? <span className={nv === 0 ? "text-emerald-600" : "text-red-600 font-medium"}>{nv > 0 ? "+" : ""}{nv}</span>
                          : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="p-3 text-right" data-testid={`sc-current-variance-${s.id}`}>
                        {s.status !== "closed"
                          ? <span className="text-zinc-300">—</span>
                          : liveVar == null
                            ? <span className="text-zinc-300 animate-pulse">…</span>
                            : cv == null
                              ? <span className="text-zinc-300">—</span>
                              : (() => {
                                  const drift = nv != null && cv !== nv;
                                  return (
                                    <span
                                      className={cv === 0 ? "text-emerald-600" : "text-red-600 font-medium"}
                                      title={drift ? `Drifted from finalize snapshot (${nv > 0 ? "+" : ""}${nv}) — ERP data changed since this count` : "Matches the finalize snapshot"}
                                    >
                                      {cv > 0 ? "+" : ""}{cv}
                                      {drift && <span className="ml-1 text-[10px] text-amber-600 align-top">▲</span>}
                                    </span>
                                  );
                                })()}
                      </td>
                      <td className="p-3 text-xs text-gray-500">{(m.counters || []).join(", ") || (s.scan_count ? "—" : "")}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => openSession(s.id)} data-testid={`sc-open-${s.id}`}>Open</Button>
                          {s.reconciliation_id && (
                            <Button size="sm" variant="outline" onClick={() => navigate(`/safety/inventory/reconcile?recon=${s.reconciliation_id}`)} data-testid={`sc-report-${s.id}`} title="View reconciliation report">
                              <ScrollText className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canManage && (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteSession(s.id)} data-testid={`sc-delete-${s.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="sc-session-detail">
            {/* Session header */}
            <div className="sf-surface p-4 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => { setActive(null); loadSessions(); }} data-testid="sc-back-btn">← Sessions</Button>
                <div>
                  <div className="font-semibold">{active.session.name}</div>
                  <div className="text-xs text-zinc-500">As of {(active.session.as_of || "").slice(0, 16).replace("T", " ")} · <Badge className={isOpen ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}>{active.session.status}</Badge></div>
                  {(active.session.merged_from_names || []).length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 w-fit" data-testid="sc-merged-from">
                      <GitCompare className="w-3 h-3 shrink-0" />
                      <span>Consolidated count · merged from: {active.session.merged_from_names.join(" + ")}</span>
                    </div>
                  )}
                  {active.session.corrected_after_finalize && (
                    <button onClick={openCorrLog} className="flex items-center gap-1.5 mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 w-fit hover:bg-amber-100 cursor-pointer" data-testid="sc-corrected-badge">
                      <AlertTriangle className="w-3 h-3 shrink-0" />
                      <span>Corrected after finalize{active.session.corrections_count ? ` · ${active.session.corrections_count} edit${active.session.corrections_count > 1 ? "s" : ""}` : ""}{active.session.last_corrected_by ? ` · last by ${active.session.last_corrected_by}` : ""} · view log</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {active.per_product && active.per_product.length > 0 && (
                  <Button size="sm" variant="outline" onClick={openCompare} data-testid="sc-compare-btn">
                    <Scale className="w-4 h-4 mr-1.5" /> Compare to ERP
                  </Button>
                )}
                {canCutover && active.per_product && active.per_product.length > 0 && (
                  <Button size="sm" variant="outline" onClick={useAsCutover} disabled={cutoverBusy} data-testid="sc-use-cutover-btn">
                    <PackageCheck className={`w-4 h-4 mr-1.5 ${cutoverBusy ? "animate-pulse" : ""}`} /> Use as cutover opening
                  </Button>
                )}
                {active.session.reconciliation_id && (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/safety/inventory/reconcile?recon=${active.session.reconciliation_id}`)} data-testid="sc-view-report">
                    <ScrollText className="w-4 h-4 mr-1.5" /> View report
                  </Button>
                )}
                {isOpen && (
                  <Button size="sm" onClick={finalize} disabled={finalizing} className="bg-orange-600 hover:bg-orange-700" data-testid="sc-finalize-btn">
                    <FileSpreadsheet className={`w-4 h-4 mr-1.5 ${finalizing ? "animate-pulse" : ""}`} /> {finalizing ? "Generating…" : "Finalize → Reconciliation"}
                  </Button>
                )}
              </div>
            </div>

            {/* Coverage progress */}
            <div className="sf-surface p-4" data-testid="sc-coverage">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium">Count coverage</span>
                <span className="text-xs text-zinc-500" data-testid="sc-coverage-label">
                  {sm.scanned_expected || 0} of {sm.expected_pallets || 0} expected pallets · <b className={(sm.coverage_pct || 0) >= 95 ? "text-emerald-600" : (sm.coverage_pct || 0) >= 70 ? "text-amber-600" : "text-red-600"}>{sm.coverage_pct || 0}%</b> of pallets
                </span>
              </div>
              <div className="h-2.5 w-full bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${(sm.coverage_pct || 0) >= 95 ? "bg-emerald-500" : (sm.coverage_pct || 0) >= 70 ? "bg-amber-500" : "bg-red-500"}`}
                  style={{ width: `${Math.min(100, sm.coverage_pct || 0)}%` }}
                  data-testid="sc-coverage-bar"
                />
              </div>
              {isOpen && (sm.coverage_pct || 0) < 95 && (
                <p className="text-[11px] text-amber-600 mt-1.5">
                  <AlertTriangle className="w-3 h-3 inline mr-0.5" />
                  {sm.missing || 0} expected pallet(s) still un-scanned — on finalize, any explained by dispatch (packing lists) are auto-marked dispatched; the rest count as missing (variance).
                </p>
              )}
              {(sm.scanned_pieces || 0) + (sm.manual_pieces || 0) > 0 && (
                <div className="mt-3 pt-3 border-t" data-testid="sc-split">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-zinc-600">QR-verified vs keyed-in</span>
                    <span className="text-xs text-zinc-500" data-testid="sc-split-label">
                      <span className="text-emerald-600 font-medium">{sm.scanned_pct || 0}% scanned</span> · <span className="text-violet-600 font-medium">{sm.manual_pct || 0}% keyed</span>
                    </span>
                  </div>
                  <div className="h-2 w-full bg-violet-200 rounded-full overflow-hidden flex">
                    <div className="h-full bg-emerald-500" style={{ width: `${sm.scanned_pct || 0}%` }} />
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-1">{(sm.scanned_pieces || 0).toLocaleString()} pcs scanned · {(sm.manual_pieces || 0).toLocaleString()} pcs entered manually</p>
                </div>
              )}
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Kpi icon={PackageCheck} label="Physical pcs" value={sm.physical_pieces} color="emerald" testId="sc-kpi-pieces" />
              <Kpi icon={ScanLine} label="Included scans" value={sm.included_scans} color="sky" testId="sc-kpi-scans" />
              <Kpi icon={PackageX} label="Missing pallets" value={sm.missing} color="red" testId="sc-kpi-missing" />
              <Kpi icon={AlertTriangle} label="Unexpected here" value={sm.unexpected_present} color="amber" testId="sc-kpi-unexpected" />
              <Kpi icon={Clock} label="Produced mid-count" value={sm.produced_during_count} color="violet" testId="sc-kpi-midcount" />
            </div>

            {/* Scan + manual entry — open for everyone; managers can also correct finalized counts */}
            {(isOpen || canManage) && (
              <div className="sf-surface p-4 space-y-4" data-testid="sc-entry-panel">
                {!isOpen && (
                  <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-1.5" data-testid="sc-corrections-banner">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span>This count is <b>finalized</b> — add / correct / delete below will recalculate the reconciliation and be logged.</span>
                  </div>
                )}
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-medium text-zinc-500 block mb-1">Zone / aisle <span className="text-red-500">(required)</span></label>
                    <Input value={zone} onChange={(e) => setZone(e.target.value)} placeholder="e.g. Aisle A / Zone 1" data-testid="sc-zone-input" />
                  </div>
                  {zone.trim() && <Badge className="bg-orange-100 text-orange-700 mb-1.5" data-testid="sc-zone-active">Scanning into: {zone.trim()}</Badge>}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-zinc-500 block mb-1">Scan / enter pallet ID</label>
                    <div className="flex gap-2">
                      <Input value={scanInput} onChange={(e) => setScanInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()} placeholder="PLT-2026-000123" data-testid="sc-scan-input" />
                      <Button onClick={doScan} disabled={busy} data-testid="sc-scan-btn"><ScanLine className="w-4 h-4" /></Button>
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-1">Damaged card? Type the pallet ID. On mobile, use the Stock Count tile to scan the QR.</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-zinc-500 block mb-1">Loose / non-palletized stock</label>
                    <div className="flex gap-2">
                      <ProductCombobox value={manualCode} onChange={setManualCode} testId="sc-manual-code" />
                      <Input value={manualQty} onChange={(e) => setManualQty(e.target.value)} type="number" placeholder="Qty" className="w-24" data-testid="sc-manual-qty" />
                      <Button variant="outline" onClick={addManual} disabled={busy} data-testid="sc-manual-btn"><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 flex-wrap">
              {[
                { k: "counted", label: `Counted (${(active.per_product || []).length})` },
                { k: "zones", label: `By zone (${(active.per_zone || []).length})` },
                { k: "scans", label: `Scans (${(active.scans || []).length})` },
                { k: "missing", label: `Missing (${sm.missing || 0})` },
                { k: "exceptions", label: `Exceptions (${sm.excluded + (sm.unexpected_present || 0)})` },
                { k: "unidentified", label: `Unidentified (${(active.unidentified || []).filter((u) => !u.resolved).length})` },
                ...(!isOpen && (active.produced_after || []).length > 0
                  ? [{ k: "produced_after", label: `Produced after count (${sm.produced_after_finalize || 0})` }]
                  : []),
                ...((active.dispatched_explained || []).length > 0
                  ? [{ k: "dispatched", label: `Dispatched (auto) (${(active.dispatched_explained || []).length})` }]
                  : []),
              ].map((t) => (
                <button key={t.k} onClick={() => setTab(t.k)} data-testid={`sc-tab-${t.k}`}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${tab === t.k ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-lg border shadow-sm overflow-auto">
              {tab === "counted" && (
                <>
                <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="sc-counted-filter">
                  <Input value={countedFilter} onChange={(e) => setCountedFilter(e.target.value)} placeholder="Filter Product…" className="h-8 w-44 text-sm" data-testid="sc-counted-filter-product" />
                  {countedFilter && (
                    <>
                      <span className="text-xs text-zinc-500" data-testid="sc-counted-filter-count">{filteredCounted.length} of {(active.per_product || []).length}</span>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setCountedFilter("")} data-testid="sc-counted-filter-clear">Clear</Button>
                    </>
                  )}
                </div>
                <table className="w-full text-sm" data-testid="sc-counted-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="p-3 text-left">Product</th><th className="p-3 text-right">Pallets</th>
                    <th className="p-3 text-right">Loose</th><th className="p-3 text-right">Physical pcs</th>
                  </tr></thead>
                  <tbody>
                    {(active.per_product || []).length === 0 && <tr><td colSpan="4" className="p-8 text-center text-gray-400">Nothing counted yet.</td></tr>}
                    {(active.per_product || []).length > 0 && filteredCounted.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-gray-400" data-testid="sc-counted-no-match">No products match the filter.</td></tr>}
                    {filteredCounted.map((r) => (
                      <tr key={r.product_code} className="border-b" data-testid={`sc-prod-${r.product_code}`}>
                        <td className="p-3 font-mono">{r.product_code}</td>
                        <td className="p-3 text-right">{r.pallets}</td>
                        <td className="p-3 text-right text-violet-700">{r.manual_pieces || 0}</td>
                        <td className="p-3 text-right font-semibold">{r.physical_pieces}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}

              {tab === "zones" && (
                <table className="w-full text-sm" data-testid="sc-zones-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="p-3 text-left">Zone</th><th className="p-3 text-right">Pallets</th>
                    <th className="p-3 text-right">Loose</th><th className="p-3 text-right">Pieces</th>
                    <th className="p-3 text-left">Counters</th>
                  </tr></thead>
                  <tbody>
                    {(active.per_zone || []).length === 0 && <tr><td colSpan="5" className="p-8 text-center text-gray-400">No scans yet. Set a Zone above, then scan — each person can count a different aisle into this same session.</td></tr>}
                    {(active.per_zone || []).map((z) => (
                      <tr key={z.zone} className="border-b" data-testid={`sc-zone-${z.zone}`}>
                        <td className="p-3 font-medium">{z.zone}</td>
                        <td className="p-3 text-right">{z.pallets}</td>
                        <td className="p-3 text-right text-violet-700">{z.loose}</td>
                        <td className="p-3 text-right font-semibold">{z.pieces}</td>
                        <td className="p-3 text-xs text-gray-500">{(z.scanners || []).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {tab === "scans" && (
                <>
                <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="sc-scans-filter">
                  <Input value={scanFilter.pallet} onChange={(e) => setScanFilter((f) => ({ ...f, pallet: e.target.value }))} placeholder="Filter Pallet ID…" className="h-8 w-44 text-sm" data-testid="sc-filter-pallet" />
                  <Input value={scanFilter.product} onChange={(e) => setScanFilter((f) => ({ ...f, product: e.target.value }))} placeholder="Filter Product…" className="h-8 w-44 text-sm" data-testid="sc-filter-product" />
                  <select value={scanFilter.zone} onChange={(e) => setScanFilter((f) => ({ ...f, zone: e.target.value }))} className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-sm text-zinc-700" data-testid="sc-filter-zone">
                    <option value="">All zones</option>
                    {scanZones.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                  <select value={scanFilter.method} onChange={(e) => setScanFilter((f) => ({ ...f, method: e.target.value }))} className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-sm text-zinc-700" data-testid="sc-filter-method">
                    <option value="">All entry methods</option>
                    <option value="qr">QR scan</option>
                    <option value="typed_id">Typed ID</option>
                    <option value="short_pick">Picked by number</option>
                    <option value="unknown">Unknown / older</option>
                    <option value="manual">Keyed (no pallet)</option>
                  </select>
                  {(scanFilter.pallet || scanFilter.product || scanFilter.zone || scanFilter.method) && (
                    <>
                      <span className="text-xs text-zinc-500" data-testid="sc-filter-count">{filteredScans.length} of {(active.scans || []).length}</span>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setScanFilter({ pallet: "", product: "", zone: "", method: "" })} data-testid="sc-filter-clear">Clear</Button>
                    </>
                  )}
                </div>
                {(() => {
                  const cm = sm.by_capture_method || {};
                  const chips = [
                    { k: "qr", label: "QR scanned", cls: "bg-emerald-100 text-emerald-700" },
                    { k: "typed_id", label: "typed ID", cls: "bg-slate-100 text-slate-600" },
                    { k: "short_pick", label: "picked by number", cls: "bg-amber-100 text-amber-700" },
                    { k: "unknown", label: "unknown / older", cls: "bg-zinc-100 text-zinc-500" },
                  ].filter((c) => (cm[c.k] || 0) > 0);
                  if (!chips.length) return null;
                  return (
                    <div className="flex flex-wrap items-center gap-1.5 mb-3 text-xs" data-testid="sc-capture-summary">
                      <span className="text-zinc-500">Entry method:</span>
                      {chips.map((c) => (
                        <button key={c.k} type="button" onClick={() => setScanFilter((f) => ({ ...f, method: f.method === c.k ? "" : c.k }))}
                          className={`px-2 py-0.5 rounded-full font-medium ${c.cls} ${scanFilter.method === c.k ? "ring-2 ring-offset-1 ring-current" : ""}`}
                          data-testid={`sc-capture-chip-${c.k}`}>
                          {cm[c.k]} {c.label}
                        </button>
                      ))}
                    </div>
                  );
                })()}
                <table className="w-full text-sm" data-testid="sc-scans-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="p-3 text-left">Pallet / Source</th><th className="p-3 text-left">Product</th>
                    <th className="p-3 text-right">Qty</th><th className="p-3 text-left">Zone</th><th className="p-3 text-right">Walk #</th><th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">By</th><th className="p-3"></th>
                  </tr></thead>
                  <tbody>
                    {filteredScans.map((sc) => (
                      <tr key={sc.id} className="border-b" data-testid={`sc-scan-${sc.id}`}>
                        <td className="p-3 font-mono text-xs">
                          {sc.pallet_id || <span className="text-violet-700">loose{sc.note ? ` · ${sc.note}` : ""}</span>}
                          {sc.pallet_id && sc.capture_method === "short_pick" && <span data-testid={`sc-scan-method-${sc.id}`} className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-300 align-middle">PICKED BY #</span>}
                          {sc.pallet_id && sc.capture_method === "typed_id" && <span data-testid={`sc-scan-method-${sc.id}`} className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-100 text-slate-600 border border-slate-300 align-middle">TYPED ID</span>}
                          {sc.pallet_id && sc.capture_method === "qr" && <span data-testid={`sc-scan-method-${sc.id}`} className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300 align-middle">QR</span>}
                        </td>
                        <td className="p-3 font-mono">{sc.product_code}</td>
                        <td className="p-3 text-right">{sc.qty}</td>
                        <td className="p-3 text-xs text-gray-600" data-testid={`sc-scan-zone-${sc.id}`}>{sc.zone || "—"}</td>
                        <td className="p-3 text-right text-xs text-gray-500" data-testid={`sc-scan-walk-${sc.id}`}>{sc.walk_no != null ? `#${sc.walk_no}` : "—"}</td>
                        <td className="p-3">
                          {sc.included
                            ? <Badge className={sc.reason === "unexpected_present" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>{sc.reason || "counted"}</Badge>
                            : <Badge className="bg-red-100 text-red-700">excluded · {sc.reason}</Badge>}
                        </td>
                        <td className="p-3 text-xs text-gray-500">{sc.scanned_by}</td>
                        <td className="p-3 text-right">
                          {(isOpen || canManage) && (
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openEdit(sc)} data-testid={`sc-edit-${sc.id}`} title="Correct product / qty">✎</Button>
                              <Button size="sm" variant="outline" className="text-red-600 h-7 px-2" onClick={() => removeScan(sc.id)} data-testid={`sc-remove-${sc.id}`}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(active.scans || []).length === 0 && <tr><td colSpan="8" className="p-8 text-center text-gray-400">No scans yet.</td></tr>}
                    {(active.scans || []).length > 0 && filteredScans.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-gray-400" data-testid="sc-scans-no-match">No scans match the filter.</td></tr>}
                  </tbody>
                </table>
                </>
              )}

              {tab === "missing" && (
                <>
                <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="sc-missing-filter">
                  <Input value={missingFilter.pallet} onChange={(e) => setMissingFilter((f) => ({ ...f, pallet: e.target.value }))} placeholder="Filter Pallet ID…" className="h-8 w-44 text-sm" data-testid="sc-missing-filter-pallet" />
                  <Input value={missingFilter.product} onChange={(e) => setMissingFilter((f) => ({ ...f, product: e.target.value }))} placeholder="Filter Product…" className="h-8 w-44 text-sm" data-testid="sc-missing-filter-product" />
                  {(missingFilter.pallet || missingFilter.product) && (
                    <>
                      <span className="text-xs text-zinc-500" data-testid="sc-missing-filter-count">{filteredMissing.length} of {(active.missing || []).length}</span>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setMissingFilter({ pallet: "", product: "" })} data-testid="sc-missing-filter-clear">Clear</Button>
                    </>
                  )}
                </div>
                <table className="w-full text-sm" data-testid="sc-missing-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="p-3 text-left">Pallet</th><th className="p-3 text-left">Product</th>
                    <th className="p-3 text-right">Qty</th><th className="p-3 text-left">PO</th><th className="p-3 text-left">Produced</th>
                  </tr></thead>
                  <tbody>
                    {(active.missing || []).length === 0 && <tr><td colSpan="5" className="p-8 text-center text-emerald-600">No missing pallets — everything expected was scanned. ✅</td></tr>}
                    {(active.missing || []).length > 0 && filteredMissing.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-gray-400" data-testid="sc-missing-no-match">No missing pallets match the filter.</td></tr>}
                    {filteredMissing.map((m) => (
                      <tr key={m.pallet_id} className="border-b bg-red-50/40" data-testid={`sc-missing-${m.pallet_id}`}>
                        <td className="p-3 font-mono text-xs">{m.pallet_id}</td>
                        <td className="p-3 font-mono">{m.product_code}</td>
                        <td className="p-3 text-right">{m.qty}</td>
                        <td className="p-3 text-xs">{m.po_no || "—"}</td>
                        <td className="p-3 text-xs text-gray-500">{(m.paired_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}

              {tab === "dispatched" && (
                <table className="w-full text-sm" data-testid="sc-dispatched-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="p-3 text-left">Pallet</th><th className="p-3 text-left">Product</th>
                    <th className="p-3 text-right">Qty retired</th><th className="p-3 text-left">Mode</th>
                    <th className="p-3 text-left">PO</th><th className="p-3 text-left">Produced</th>
                  </tr></thead>
                  <tbody>
                    <tr><td colSpan="6" className="px-3 py-2 text-xs text-zinc-500 bg-emerald-50/60">Expected pallets that weren't scanned but are explained by dispatch (packing lists) since the cutover — auto-marked <b>dispatched</b> (FIFO, oldest first) so they won't show as "missing" in future counts. These are NOT shrinkage.</td></tr>
                    {(active.dispatched_explained || []).map((d) => (
                      <tr key={d.pallet_id} className="border-b bg-emerald-50/30" data-testid={`sc-dispatched-${d.pallet_id}`}>
                        <td className="p-3 font-mono text-xs">{d.pallet_id}</td>
                        <td className="p-3 font-mono">{d.product_code}</td>
                        <td className="p-3 text-right">{d.qty}</td>
                        <td className="p-3 text-xs">{d.mode === "partial" ? `partial (${d.remaining} left on-hand)` : "full pallet"}</td>
                        <td className="p-3 text-xs">{d.po_no || "—"}</td>
                        <td className="p-3 text-xs text-gray-500">{(d.paired_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {tab === "produced_after" && (
                <table className="w-full text-sm" data-testid="sc-produced-after-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="p-3 text-left">Pallet</th><th className="p-3 text-left">Product</th>
                    <th className="p-3 text-right">Qty</th><th className="p-3 text-left">PO</th>
                    <th className="p-3 text-left">State</th><th className="p-3 text-left">Produced</th>
                  </tr></thead>
                  <tbody>
                    <tr><td colSpan="6" className="px-3 py-2 text-xs text-zinc-500 bg-sky-50/60">Pallets produced <b>after</b> this count was finalized ({fmtDateTime(active.session.closed_at)}) — new stock not part of the snapshot.</td></tr>
                    {(active.produced_after || []).length === 0 && <tr><td colSpan="6" className="p-8 text-center text-gray-400">Nothing produced since this count was finalized.</td></tr>}
                    {(active.produced_after || []).map((p) => (
                      <tr key={p.pallet_id} className="border-b bg-sky-50/30" data-testid={`sc-produced-after-${p.pallet_id}`}>
                        <td className="p-3 font-mono text-xs">{p.pallet_id}</td>
                        <td className="p-3 font-mono">{p.product_code}</td>
                        <td className="p-3 text-right">{p.qty}</td>
                        <td className="p-3 text-xs">{p.po_no || "—"}</td>
                        <td className="p-3 text-xs text-gray-500">{p.state || "—"}</td>
                        <td className="p-3 text-xs text-gray-500">{fmtDateTime(p.paired_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {tab === "exceptions" && (
                <div className="divide-y" data-testid="sc-exceptions">
                  <ExceptionBlock title="Unexpected present (system says gone, but physically here)" color="amber" rows={active.exceptions?.unexpected_present} />
                  <ExceptionBlock title="Produced during count (excluded from this snapshot)" color="violet" rows={active.exceptions?.produced_during_count} />
                  <ExceptionBlock title="Not produced yet (empty pallet — excluded)" color="gray" rows={active.exceptions?.not_produced} />
                  <ExceptionBlock title="Voided (excluded)" color="red" rows={active.exceptions?.voided} />
                </div>
              )}

              {tab === "unidentified" && (
                <div className="divide-y" data-testid="sc-unidentified">
                  {(active.unidentified || []).length === 0 && (
                    <div className="p-8 text-center text-gray-400">No unidentified items were photographed in this count.</div>
                  )}
                  {(active.unidentified || []).some((u) => !u.resolved) && (
                    <div className="p-3 bg-amber-50/60 flex items-center gap-2 flex-wrap sticky top-0 z-10" data-testid="sc-bulk-bar">
                      <span className="text-xs font-medium text-amber-800" data-testid="sc-bulk-selcount">{bulkSel.size} selected</span>
                      <button
                        className="text-[11px] text-orange-700 underline"
                        onClick={() => setBulkSel(new Set((active.unidentified || []).filter((u) => !u.resolved).map((u) => u.id)))}
                        data-testid="sc-bulk-selectall">Select all pending</button>
                      <button className="text-[11px] text-zinc-500 underline" onClick={() => setBulkSel(new Set())} data-testid="sc-bulk-clear">Clear</button>
                      <div className="w-48"><ProductCombobox value={bulkCode} onChange={setBulkCode} testId="sc-bulk-code" /></div>
                      <Input type="number" placeholder="Qty each (pcs)" className="w-32" value={bulkQty} onChange={(e) => setBulkQty(e.target.value)} data-testid="sc-bulk-qty" />
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={bulkBusy || bulkSel.size === 0} onClick={bulkResolve} data-testid="sc-bulk-resolve-btn">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve selected
                      </Button>
                    </div>
                  )}
                  {(active.unidentified || []).map((u) => {
                    const pics = u.photos && u.photos.length ? u.photos : (u.file_id ? [u.file_id] : []);
                    const after = u.after_scan ? (u.after_scan.product_code || u.after_scan.pallet_id) : null;
                    const before = u.before_scan ? (u.before_scan.product_code || u.before_scan.pallet_id) : null;
                    const ri = resolveInputs[u.id] || {};
                    return (
                      <div key={u.id} className="p-4 flex gap-4 items-start" data-testid={`sc-unident-${u.id}`}>
                        {!u.resolved && (
                          <input type="checkbox" className="mt-1 w-4 h-4 accent-orange-600 shrink-0" checked={bulkSel.has(u.id)} onChange={() => toggleBulk(u.id)} data-testid={`sc-unident-check-${u.id}`} />
                        )}
                        <div className="flex gap-2 shrink-0">
                          {pics.map((fid) => (
                            <a key={fid} href={photoUrl(fid)} target="_blank" rel="noreferrer">
                              <img src={photoUrl(fid)} alt="unidentified item" className="w-24 h-24 object-cover rounded-md border hover:ring-2 hover:ring-orange-400" />
                            </a>
                          ))}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-zinc-800">{u.zone} · walk #{u.zone_seq || 0}</div>
                          <div className="text-xs text-zinc-600 mt-0.5" data-testid={`sc-unident-anchor-${u.id}`}>
                            {after ? <>after <span className="font-mono">{after}</span></> : "start of zone"}
                            {before ? <> → before <span className="font-mono">{before}</span></> : " (last so far)"}
                          </div>
                          {u.note && <div className="text-xs text-zinc-500 mt-0.5"><span className="text-zinc-400">Location:</span> {u.note}</div>}
                          <div className="text-[11px] text-zinc-400 mt-0.5">by {u.created_by} · {fmtDateTime(u.created_at)}</div>

                          {u.resolved ? (
                            (u.resolution_type === "note" || (u.resolved_note && !u.resolved_product_code)) ? (
                              <Badge className="bg-amber-100 text-amber-800 mt-2 whitespace-normal text-left" data-testid={`sc-unident-resolved-${u.id}`}>
                                <StickyNote className="w-3 h-3 mr-1 inline shrink-0" /> Noted (not counted): {u.resolved_note}
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700 mt-2" data-testid={`sc-unident-resolved-${u.id}`}>
                                Resolved → {u.resolved_product_code} · {u.resolved_qty} pcs{u.resolved_pallet_id ? ` · ${u.resolved_pallet_id}` : ""}
                              </Badge>
                            )
                          ) : (
                            <div className="mt-3">
                              {!isOpen && <span className="text-[11px] text-zinc-400 block mb-2">Session finalized — resolving updates the report.</span>}
                              <div className="inline-flex rounded-md border p-0.5 mb-2 bg-zinc-50">
                                <button onClick={() => setRi(u.id, { mode: "product" })} data-testid={`sc-unident-mode-product-${u.id}`}
                                  className={`px-2.5 py-1 text-xs font-medium rounded ${(ri.mode || "product") === "product" ? "bg-emerald-600 text-white" : "text-zinc-600"}`}>Identify product</button>
                                <button onClick={() => setRi(u.id, { mode: "pallet" })} data-testid={`sc-unident-mode-pallet-${u.id}`}
                                  className={`px-2.5 py-1 text-xs font-medium rounded ${ri.mode === "pallet" ? "bg-sky-600 text-white" : "text-zinc-600"}`}>Pallet ID</button>
                                <button onClick={() => setRi(u.id, { mode: "note" })} data-testid={`sc-unident-mode-note-${u.id}`}
                                  className={`px-2.5 py-1 text-xs font-medium rounded ${ri.mode === "note" ? "bg-amber-500 text-white" : "text-zinc-600"}`}>Note only</button>
                              </div>
                              {(ri.mode || "product") === "product" ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="w-48"><ProductCombobox value={ri.code || ""} onChange={(c) => setRi(u.id, { code: c })} testId={`sc-unident-code-${u.id}`} /></div>
                                  <Input type="number" placeholder="Qty (pcs)" className="w-28" value={ri.qty || ""} onChange={(e) => setRi(u.id, { qty: e.target.value })} data-testid={`sc-unident-qty-${u.id}`} />
                                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => resolveUnident(u.id)} data-testid={`sc-unident-resolve-${u.id}`}>
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                                  </Button>
                                  {canManage && (
                                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteUnident(u.id)} data-testid={`sc-unident-del-${u.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              ) : ri.mode === "pallet" ? (
                                <div className="flex items-start gap-2 flex-wrap">
                                  <Input placeholder="Pallet short number (e.g. 042)" className="w-56" value={ri.shortId || ""} onChange={(e) => setRi(u.id, { shortId: e.target.value, palletCandidates: null })} onKeyDown={(e) => { if (e.key === "Enter") resolvePallet(u.id); }} data-testid={`sc-unident-shortid-${u.id}`} />
                                  <Button size="sm" className="bg-sky-600 hover:bg-sky-700" disabled={busy} onClick={() => resolvePallet(u.id)} data-testid={`sc-unident-resolvepallet-${u.id}`}>
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Find &amp; link pallet
                                  </Button>
                                  {canManage && (
                                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteUnident(u.id)} data-testid={`sc-unident-del-${u.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <span className="text-[11px] text-zinc-400 w-full">Links to the real pallet — product & qty come from it, counted like a scan.</span>
                                  {(ri.palletCandidates || []).length > 0 && (
                                    <div className="w-full flex flex-col gap-1 mt-1" data-testid={`sc-unident-pallet-cands-${u.id}`}>
                                      <span className="text-[11px] text-zinc-500">Multiple match — pick the right pallet:</span>
                                      {ri.palletCandidates.map((c) => (
                                        <button key={c.id} onClick={() => resolvePallet(u.id, c.id)} disabled={busy} data-testid={`sc-unident-pallet-cand-${u.id}-${c.id}`}
                                          className="text-left px-2 py-1 rounded border border-sky-200 bg-sky-50 hover:bg-sky-100 text-xs">
                                          <span className="font-mono">{c.pallet_id}</span> · {c.product_code} · {c.qty} pcs · {c.state}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-start gap-2 flex-wrap">
                                  <Input placeholder="e.g. Empty pallet · scrap · not our product · follow-up" className="w-80" value={ri.noteText || ""} onChange={(e) => setRi(u.id, { noteText: e.target.value })} data-testid={`sc-unident-notetext-${u.id}`} />
                                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={() => resolveNote(u.id)} data-testid={`sc-unident-savenote-${u.id}`}>
                                    <StickyNote className="w-4 h-4 mr-1" /> Save note &amp; close
                                  </Button>
                                  {canManage && (
                                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteUnident(u.id)} data-testid={`sc-unident-del-${u.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <span className="text-[11px] text-zinc-400 w-full">Closes this item with a note — nothing is added to the count.</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Compare to ERP preview */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="sc-compare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Scale className="w-5 h-5 text-orange-600" /> Scanned physical vs current ERP</DialogTitle>
            <DialogDescription>Shows the <b>current position</b> — variance &amp; status recomputed against today's ERP data (parsed packing lists, dispatches, adjustments). No data is changed.</DialogDescription>
          </DialogHeader>
          {compareBusy ? (
            <div className="py-12 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" /></div>
          ) : compareData ? (
            <div className="flex-1 overflow-auto">
              <div className="text-xs text-zinc-500 mb-2 px-1 space-y-1">
                <div>
                  {compareData.finalized && <b className="text-emerald-700">Current position · </b>}
                  <b className="text-zinc-700">{compareData.summary?.variances || 0}</b> product{(compareData.summary?.variances || 0) === 1 ? "" : "s"} differ{(compareData.summary?.variances || 0) === 1 ? "s" : ""} · net variance <b className={`${(compareData.summary?.net_variance ?? 0) === 0 ? "text-emerald-600" : "text-zinc-700"}`}>{(compareData.summary?.net_variance ?? 0) > 0 ? "+" : ""}{compareData.summary?.net_variance || 0} pcs</b> · as of {fmtDateTime(compareData.as_of)}
                </div>
                {compareData.finalized && compareSnapshot && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium" data-testid="sc-compare-snapshot-badge">
                      At finalize{compareSnapshot.finalized_at ? ` (${fmtDateTime(compareSnapshot.finalized_at)})` : ""}: net variance {(compareSnapshot.summary?.net_variance ?? 0) > 0 ? "+" : ""}{compareSnapshot.summary?.net_variance || 0} pcs · {compareSnapshot.summary?.variances || 0} product{(compareSnapshot.summary?.variances || 0) === 1 ? "" : "s"}
                    </span>
                    {(compareSnapshot.summary?.net_variance || 0) !== (compareData.summary?.net_variance || 0) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[11px] font-medium" data-testid="sc-compare-drift-note">
                        <RefreshCw className="w-3 h-3" /> Drifted since finalize — ERP data for this period changed
                      </span>
                    )}
                  </div>
                )}
              </div>
              <table className="w-full text-sm border rounded-md">
                <thead className="bg-gray-50 border-b sticky top-0"><tr>
                  <th className="p-2 text-left">Product</th>
                  <th className="p-2 text-right">Scanned</th>
                  <th className="p-2 text-right">ERP</th>
                  <th className="p-2 text-right">Variance</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Reconciliation note</th>
                  {canCutover && <th className="p-2 text-left">Fix</th>}
                </tr></thead>
                <tbody data-testid="sc-compare-table">
                  {(compareData.items || []).map((it) => (
                    <tr key={it.product_code} className="border-b" data-testid={`sc-compare-${it.product_code}`}>
                      <td className="p-2 font-mono">{it.product_code}</td>
                      <td className="p-2 text-right">{it.physical_pieces}</td>
                      <td className="p-2 text-right">{it.erp_pieces}</td>
                      <td className={`p-2 text-right font-semibold ${it.variance === 0 ? "text-emerald-600" : it.variance > 0 ? "text-blue-600" : "text-red-600"}`}>{it.variance > 0 ? "+" : ""}{it.variance}</td>
                      <td className="p-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${it.status === "match" ? "bg-emerald-100 text-emerald-700" : it.status === "erp_short" ? "bg-amber-100 text-amber-700" : it.status === "erp_excess" ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-700"}`}>
                          {it.status}
                        </span>
                      </td>
                      <td className="p-2">
                        {it.product_code !== "—" ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              className="w-40 text-[11px] border rounded px-1.5 py-1"
                              placeholder="personal note…"
                              value={reconNoteDraft[it.product_code] ?? reconNotes[it.product_code] ?? ""}
                              onChange={(e) => setReconNoteDraft((p) => ({ ...p, [it.product_code]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === "Enter") saveReconNote(it.product_code); }}
                              data-testid={`sc-recon-note-input-${it.product_code}`}
                            />
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]"
                              disabled={savingNote === it.product_code || (reconNoteDraft[it.product_code] === undefined)}
                              onClick={() => saveReconNote(it.product_code)}
                              data-testid={`sc-recon-note-save-${it.product_code}`}
                              title="Save your personal reconciliation note"><StickyNote className="w-3 h-3" /></Button>
                          </div>
                        ) : <span className="text-zinc-300 text-xs">—</span>}
                        {(resolHistory[it.product_code] || []).length > 0 && (
                          <div className="mt-1 space-y-0.5" data-testid={`sc-audit-trail-${it.product_code}`}>
                            {(resolHistory[it.product_code] || []).slice(0, 3).map((h, hi) => (
                              <div key={hi} className="text-[10px] text-zinc-500 leading-tight" title={h.reason}>
                                <span className={`font-medium ${h.mode === "opening" ? "text-sky-600" : "text-amber-600"}`}>{h.qty > 0 ? "+" : ""}{h.qty} {h.mode === "opening" ? "opening" : "adj"}</span>
                                {" · "}{h.user_name}{" · "}{fmtIST(h.timestamp)}
                                {h.reason ? <span className="text-zinc-400"> · “{h.reason.length > 28 ? h.reason.slice(0, 28) + "…" : h.reason}”</span> : null}
                              </div>
                            ))}
                            {(resolHistory[it.product_code] || []).length > 3 && (
                              <div className="text-[10px] text-zinc-400">+{(resolHistory[it.product_code] || []).length - 3} more</div>
                            )}
                          </div>
                        )}
                      </td>
                      {canCutover && (
                        <td className="p-2">
                          {it.variance !== 0 && it.product_code !== "—" ? (
                            <div className="flex items-center gap-1">
                              {it.variance > 0 && (
                                <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" disabled={resolvingVar === it.product_code}
                                  onClick={() => openResolve(it, "opening")} data-testid={`sc-resolve-opening-${it.product_code}`}
                                  title="Add the missing pieces to the cutover opening balance">+ Opening</Button>
                              )}
                              <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" disabled={resolvingVar === it.product_code}
                                onClick={() => openResolve(it, "adjustment")} data-testid={`sc-resolve-adjust-${it.product_code}`}
                                title="Post a manual inventory adjustment for the difference">Adjust</Button>
                            </div>
                          ) : <span className="text-zinc-300 text-xs">—</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                  {(compareData.items || []).length === 0 && <tr><td colSpan={canCutover ? 6 : 5} className="p-6 text-center text-gray-400">Nothing to compare.</td></tr>}
                </tbody>
              </table>
            </div>
          ) : null}
          <DialogFooter><Button variant="outline" onClick={() => setCompareOpen(false)} data-testid="sc-compare-close">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resolveDlg.open} onOpenChange={(o) => setResolveDlg((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md" data-testid="sc-resolve-dialog">
          <DialogHeader>
            <DialogTitle>{resolveDlg.mode === "opening" ? "Add to opening balance" : "Adjust quantity"}</DialogTitle>
            <DialogDescription>
              {resolveDlg.item ? (
                <>Resolve <b>{resolveDlg.item.product_code}</b> by {resolveDlg.mode === "opening" ? "adding to the cutover opening balance" : "posting a manual adjustment"} of{" "}
                <b>{resolveDlg.item.variance > 0 ? "+" : ""}{resolveDlg.item.variance} pcs</b>. This updates ERP so the variance clears.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="sc-resolve-note">Reason / note <span className="text-red-500">(required)</span></Label>
            <div className="flex flex-wrap gap-1.5" data-testid="sc-reason-presets">
              {reasonPresets.map((p) => (
                <span key={p.id} className="group inline-flex items-center rounded-full border border-zinc-300 bg-zinc-50 hover:bg-zinc-100 text-[11px] pl-2 pr-1 py-0.5 cursor-pointer transition-colors"
                  data-testid={`sc-reason-preset-${p.id}`}>
                  <button type="button" onClick={() => applyPreset(p.label)} className="mr-1" title="Add this reason to the note">{p.label}</button>
                  <button type="button" onClick={() => deletePreset(p.id)} title="Remove this quick reason"
                    className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500 transition-opacity"
                    data-testid={`sc-reason-preset-delete-${p.id}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                className="flex-1 text-[11px] border rounded px-2 py-1"
                placeholder="Add your own quick reason…"
                value={newPresetLabel}
                onChange={(e) => setNewPresetLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPreset(); } }}
                data-testid="sc-reason-preset-add-input"
              />
              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={!newPresetLabel.trim() || addingPreset}
                onClick={addPreset} data-testid="sc-reason-preset-add-btn"><Plus className="w-3 h-3 mr-0.5" />Add</Button>
            </div>
            <textarea
              id="sc-resolve-note"
              className="w-full min-h-[80px] text-sm border rounded-md px-2 py-1.5"
              placeholder="e.g. Found 3 extra pallets in Bay 4 not scanned; verified by supervisor"
              value={resolveNoteText}
              onChange={(e) => setResolveNoteText(e.target.value)}
              data-testid="sc-resolve-note-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDlg({ open: false, item: null, mode: null })} data-testid="sc-resolve-cancel">Cancel</Button>
            <Button onClick={confirmResolve} disabled={!resolveNoteText.trim() || resolvingVar} data-testid="sc-resolve-confirm">
              {resolvingVar ? "Saving…" : "Confirm & resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <CompareSessionsDialog open={cmpOpen} onOpenChange={setCmpOpen} data={cmpData} busy={cmpBusy} onMerge={canManage ? mergeCompared : null} />

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-md" data-testid="sc-edit-dialog">
          <DialogHeader>
            <DialogTitle>Correct entry</DialogTitle>
            <DialogDescription>
              {editRow?.pallet_id ? <>Pallet <span className="font-mono">{editRow.pallet_id}</span></> : "Loose / keyed entry"}
              {!isOpen && " · this count is finalized — saving will refresh the reconciliation."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs font-medium text-zinc-500 block mb-1">Product code</label>
              <ProductCombobox value={editCode} onChange={setEditCode} testId="sc-edit-code" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 block mb-1">Quantity (pieces)</label>
              <Input type="number" value={editQty} onChange={(e) => setEditQty(e.target.value)} data-testid="sc-edit-qty" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)} data-testid="sc-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="sc-edit-save">Save correction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={corrLogOpen} onOpenChange={setCorrLogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="sc-corr-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5 text-amber-600" /> Corrections log</DialogTitle>
            <DialogDescription>Every add / correct / delete made to this count after it was finalized.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {corrBusy ? (
              <div className="py-10 flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600" /></div>
            ) : corrLog.length === 0 ? (
              <div className="py-10 text-center text-sm text-zinc-400">No corrections recorded.</div>
            ) : (
              <ul className="space-y-2">
                {corrLog.map((e, i) => (
                  <li key={i} className="border rounded-md p-2.5 bg-zinc-50" data-testid={`sc-corr-log-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-zinc-800">{e.label}</span>
                      <span className="text-[11px] text-zinc-400">{fmtDateTime(e.at)}</span>
                    </div>
                    {e.note && <div className="text-xs text-zinc-600 mt-0.5 font-mono">{e.note}</div>}
                    <div className="text-[11px] text-zinc-400 mt-0.5">by {e.by}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCorrLogOpen(false)} data-testid="sc-corr-log-close">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use-as-cutover: pick effective date/time */}
      <Dialog open={cutoverDialog} onOpenChange={(o) => !o && !cutoverBusy && setCutoverDialog(false)}>
        <DialogContent data-testid="sc-cutover-dialog">
          <DialogHeader>
            <DialogTitle>Use count as cutover opening</DialogTitle>
            <DialogDescription>
              This creates a DRAFT cutover opening-balance baseline from the count's per-product figures. You'll review & activate it from Cutover Setup. The <b>effective date &amp; time</b> below is the cutover boundary — pallets produced after it count as new production.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1 space-y-2">
            <Label className="text-xs text-zinc-500">Cutover effective date &amp; time (IST)</Label>
            <Input
              type="datetime-local"
              value={cutoverEff}
              onChange={(e) => setCutoverEff(e.target.value)}
              data-testid="sc-cutover-datetime"
            />
            <p className="text-[11px] text-zinc-500">
              Defaults to when this count was frozen ({fmtDateTime(active?.session?.as_of)}). Keep this unless you have a reason to change it — it avoids double-counting stock produced earlier the same day.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCutoverDialog(false)} disabled={cutoverBusy}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={confirmUseAsCutover} disabled={cutoverBusy} data-testid="sc-cutover-confirm">
              {cutoverBusy ? "Creating…" : "Create draft cutover"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SafetyShell>
  );
}

function Kpi({ icon: Icon, label, value, color, testId }) {
  const pal = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <div className={`border rounded-lg p-3 ${pal[color]}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1"><span className="text-[11px] uppercase tracking-wide opacity-80">{label}</span><Icon className="w-4 h-4 opacity-70" /></div>
      <div className="text-2xl font-bold">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function ExceptionBlock({ title, color, rows }) {
  const list = rows || [];
  const dot = { amber: "bg-amber-500", violet: "bg-violet-500", gray: "bg-gray-400", red: "bg-red-500" }[color];
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full ${dot}`}></span>
        <span className="text-sm font-medium">{title}</span>
        <span className="text-xs text-gray-400">({list.length})</span>
      </div>
      {list.length === 0 ? <p className="text-xs text-gray-400 pl-4">None</p> : (
        <div className="flex flex-wrap gap-1.5 pl-4">
          {list.map((r) => (
            <span key={r.id} className="text-xs font-mono bg-gray-100 rounded px-1.5 py-0.5">{r.pallet_id} · {r.product_code} · {r.qty}</span>
          ))}
        </div>
      )}
    </div>
  );
}


function CompareSessionsDialog({ open, onOpenChange, data, busy, onMerge }) {
  const [showAll, setShowAll] = useState(false);
  const sessions = data?.sessions || [];
  const n = sessions.length;
  const ps = data?.pallet_summary || {};
  const combined = data?.combined || {};
  const matrix = data?.pallet_matrix || [];
  const shownMatrix = showAll ? matrix : matrix.filter((r) => r.discrepancy);
  const missing = combined.missing_after_combine || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="sc-compare-sessions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitCompare className="w-5 h-5 text-orange-600" /> Compare stock counts</DialogTitle>
          <DialogDescription>Cross-check independent counts of the same stock so no pallet slips through. Presence & quantity mismatches are flagged; combining every count shows what was still caught by nobody.</DialogDescription>
        </DialogHeader>

        {busy ? (
          <div className="py-16 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" /></div>
        ) : !data ? null : (
          <div className="flex-1 overflow-auto space-y-5 pr-1">

            {/* Session legend */}
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(n, 3)}, minmax(0, 1fr))` }} data-testid="sc-cmp-legend">
              {sessions.map((s, i) => (
                <div key={s.id} className="border rounded-lg p-3 bg-zinc-50" data-testid={`sc-cmp-session-${i}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-5 h-5 rounded bg-orange-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">#{i + 1}</span>
                    <span className="text-sm font-semibold truncate" title={s.name}>{s.name}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500">{fmtDateTime(s.as_of)} · {s.status}</div>
                  <div className="flex gap-3 mt-1.5 text-xs">
                    <span><b>{s.scanned_pallets}</b> pallets</span>
                    <span><b>{Number(s.physical_pieces || 0).toLocaleString()}</b> pcs</span>
                    <span className={s.coverage_pct >= 95 ? "text-emerald-600" : s.coverage_pct >= 70 ? "text-amber-600" : "text-red-600"}>{s.coverage_pct}%</span>
                  </div>
                  {(s.counters || []).length > 0 && (
                    <div className="flex items-center gap-1 mt-1 text-[11px] text-zinc-400"><Users className="w-3 h-3" /> {s.counters.join(", ")}</div>
                  )}
                </div>
              ))}
            </div>

            {/* Combined coverage — the payoff */}
            <div className={`rounded-lg border p-4 ${combined.missing_count > 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="sc-cmp-combined">
              <div className="flex items-center gap-2">
                {combined.missing_count > 0 ? <PackageX className="w-5 h-5 text-red-600" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                <span className="text-sm font-semibold">
                  {combined.missing_count > 0
                    ? <>Combining all {n} counts, <b data-testid="sc-cmp-missing-count">{combined.missing_count}</b> of {combined.expected_pallets} expected pallets were caught by <u>nobody</u>.</>
                    : <>All {combined.expected_pallets} expected pallets were caught by at least one counter. ✅</>}
                </span>
              </div>
              {combined.missing_count > 0 && (
                <div className="mt-2 max-h-32 overflow-auto border rounded bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-red-50 sticky top-0"><tr><th className="p-1.5 text-left">Pallet</th><th className="p-1.5 text-left">Product</th><th className="p-1.5 text-right">Qty</th><th className="p-1.5 text-left">PO</th></tr></thead>
                    <tbody>
                      {missing.slice(0, 200).map((m) => (
                        <tr key={m.pallet_id} className="border-b" data-testid={`sc-cmp-missing-${m.pallet_id}`}>
                          <td className="p-1.5 font-mono">{m.pallet_id}</td><td className="p-1.5 font-mono">{m.product_code}</td>
                          <td className="p-1.5 text-right">{m.qty}</td><td className="p-1.5">{m.po_no || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pallet matrix */}
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-sm font-semibold flex items-center gap-1.5"><PackageX className="w-4 h-4 text-orange-600" /> Pallet-by-pallet</span>
                <Badge className="bg-red-100 text-red-700">{ps.presence_discrepancy || 0} missed by some</Badge>
                <Badge className="bg-amber-100 text-amber-700">{ps.qty_discrepancy || 0} qty mismatch</Badge>
                <Badge className="bg-emerald-100 text-emerald-700">{ps.in_all || 0} caught by all</Badge>
                <button className="text-[11px] text-orange-700 underline ml-auto" onClick={() => setShowAll((v) => !v)} data-testid="sc-cmp-toggle-all">
                  {showAll ? "Show only discrepancies" : `Show all ${ps.total_unique || 0} pallets`}
                </button>
              </div>
              <div className="border rounded-md max-h-80 overflow-auto">
                <table className="w-full text-xs" data-testid="sc-cmp-matrix">
                  <thead className="bg-gray-50 border-b sticky top-0"><tr>
                    <th className="p-2 text-left">Pallet</th><th className="p-2 text-left">Product</th>
                    {sessions.map((s, i) => <th key={s.id} className="p-2 text-center">#{i + 1}</th>)}
                    <th className="p-2 text-left">Issue</th>
                  </tr></thead>
                  <tbody>
                    {shownMatrix.length === 0 && <tr><td colSpan={n + 3} className="p-6 text-center text-emerald-600">No discrepancies — every counter agrees. ✅</td></tr>}
                    {shownMatrix.slice(0, 1000).map((r) => (
                      <tr key={r.pallet_id} className={`border-b ${r.discrepancy ? "bg-red-50/50" : ""}`} data-testid={`sc-cmp-row-${r.pallet_id}`}>
                        <td className="p-2 font-mono">{r.pallet_id}</td>
                        <td className="p-2 font-mono">{r.product_code || "—"}</td>
                        {r.per_session.map((p, i) => (
                          <td key={i} className="p-2 text-center">
                            {p.present
                              ? <span className="text-emerald-700" title={`${p.qty} pcs · ${p.zone}`}>✓ {p.qty}</span>
                              : <span className="text-red-500 font-semibold">✗</span>}
                          </td>
                        ))}
                        <td className="p-2">
                          {r.discrepancy_types.includes("presence") && <Badge className="bg-red-100 text-red-700 mr-1">missed</Badge>}
                          {r.discrepancy_types.includes("qty") && <Badge className="bg-amber-100 text-amber-700">qty</Badge>}
                          {!r.discrepancy && <span className="text-emerald-600">match</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Product variance */}
            <div>
              <span className="text-sm font-semibold flex items-center gap-1.5 mb-2"><PackageCheck className="w-4 h-4 text-orange-600" /> Per-product pieces</span>
              <div className="border rounded-md max-h-64 overflow-auto">
                <table className="w-full text-xs" data-testid="sc-cmp-products">
                  <thead className="bg-gray-50 border-b sticky top-0"><tr>
                    <th className="p-2 text-left">Product</th>
                    {sessions.map((s, i) => <th key={s.id} className="p-2 text-right">#{i + 1}</th>)}
                    <th className="p-2 text-right">Δ</th>
                  </tr></thead>
                  <tbody>
                    {(data.product_variance || []).map((r) => (
                      <tr key={r.product_code} className={`border-b ${r.has_variance ? "bg-amber-50/50" : ""}`} data-testid={`sc-cmp-prod-${r.product_code}`}>
                        <td className="p-2 font-mono">{r.product_code}</td>
                        {r.per_session.map((v, i) => <td key={i} className="p-2 text-right">{v.toLocaleString()}</td>)}
                        <td className={`p-2 text-right font-semibold ${r.has_variance ? "text-red-600" : "text-emerald-600"}`}>{r.variance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Zone variance */}
            <div>
              <span className="text-sm font-semibold flex items-center gap-1.5 mb-2"><MapPin className="w-4 h-4 text-orange-600" /> Per-zone pieces</span>
              <div className="border rounded-md max-h-64 overflow-auto">
                <table className="w-full text-xs" data-testid="sc-cmp-zones">
                  <thead className="bg-gray-50 border-b sticky top-0"><tr>
                    <th className="p-2 text-left">Zone</th>
                    {sessions.map((s, i) => <th key={s.id} className="p-2 text-right">#{i + 1}</th>)}
                    <th className="p-2 text-right">Δ</th>
                  </tr></thead>
                  <tbody>
                    {(data.zone_variance || []).map((r) => (
                      <tr key={r.zone} className={`border-b ${r.has_variance ? "bg-amber-50/50" : ""}`} data-testid={`sc-cmp-zone-${r.zone}`}>
                        <td className="p-2 font-medium">{r.zone}</td>
                        {r.per_session.map((v, i) => <td key={i} className="p-2 text-right">{(v.pieces || 0).toLocaleString()}</td>)}
                        <td className={`p-2 text-right font-semibold ${r.has_variance ? "text-red-600" : "text-emerald-600"}`}>{r.piece_variance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {onMerge && data && !busy ? (
            <Button onClick={onMerge} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sc-cmp-merge-btn">
              <GitCompare className="w-4 h-4 mr-1.5" /> Merge into master count
            </Button>
          ) : <span />}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sc-cmp-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
